<script setup>
/* Contadores + chips de oferta + cards vivos dos bancos, replicando a UX do
   MAZAM AI: Ofertado (borda verde, MÁX. LIBERADO), Consultando (elapsed +
   média + barra), Teimosinha (Tentativa X/Y + Atualizar status),
   Indisponível (motivo + Ressimular). Clicar num card ofertado abre o painel
   com TODAS as ofertas daquele banco.

   v44 (pedido do Kairo): sem o "R$ x,xx/m" em lugar NENHUM; cada linha de
   oferta tem SIMULAR (amarelo, card ao lado com chips de prazo, estilo do
   outro simulador) + COPIAR OFERTA (azul) + DIGITAR (verde); card com link
   de autorização ganha COPIAR LINK. */
import { computed, onBeforeUnmount, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import simuladorApi from 'dashboard/api/simuladorApi';
import {
  SIM_BANKS,
  BANK_STYLE,
  BANK_STATE_LABEL,
  STATUS_COUNTERS,
  brl,
} from '../bancos.js';

const props = defineProps({
  // linhas de GET simulacoes/<id> → bancos[]
  bancos: { type: Array, default: () => [] },
  // id da simulação ativa — necessário pro SIMULAR por valor
  simulacaoId: { type: [String, Number], default: null },
});
const emit = defineEmits(['ressimular', 'atualizar-status', 'digitar']);

const mostrarCards = ref(true);
const aberto = ref(''); // banco do painel de ofertas expandido

/* Ticker de 1s para o "consultando há Ns" — só o cliente conta o tempo. */
const agora = ref(Date.now());
const tick = setInterval(() => {
  agora.value = Date.now();
}, 1000);
onBeforeUnmount(() => clearInterval(tick));

const porBanco = computed(() =>
  Object.fromEntries((props.bancos || []).map(b => [b.banco, b]))
);
const dadosDe = b => porBanco.value[b] || null;
const estadoDe = b => dadosDe(b)?.status || 'aguardando';
const linkDe = b => dadosDe(b)?.link || '';
const emTeimosinha = b => {
  const d = dadosDe(b);
  return d && d.status === 'consultando' && (d.tentativa || 0) > 1;
};

const elapsedDe = b => {
  const d = dadosDe(b);
  if (!d?.iniciadoEm) return 0;
  return Math.max(0, Math.round((agora.value - new Date(d.iniciadoEm).getTime()) / 1000));
};
const mediaDe = b => Math.round(dadosDe(b)?.mediaS || 0) || null;
const progressoDe = b => {
  const media = mediaDe(b) || 60;
  return Math.min(95, Math.round((elapsedDe(b) / media) * 100));
};

const melhorOferta = b => {
  const ofs = dadosDe(b)?.ofertas || [];
  if (!ofs.length) return null;
  return [...ofs].sort(
    (x, y) => (y.valor_liberado || 0) - (x.valor_liberado || 0)
  )[0];
};
const ofertasOrdenadas = b =>
  [...(dadosDe(b)?.ofertas || [])].sort(
    (x, y) => (y.valor_liberado || 0) - (x.valor_liberado || 0)
  );

/* Rótulo curto dos detalhes de uma oferta (com/sem seguro, tabela…). */
const detalheResumo = of => {
  const d = of?.detalhes || {};
  const partes = [];
  if (d.seguro === true || /com seguro/i.test(String(d.tabela || d.descricao || '')))
    partes.push('com seguro');
  else if (d.seguro === false) partes.push('sem seguro');
  if (d.tabela) partes.push(String(d.tabela));
  else if (d.descricao) partes.push(String(d.descricao));
  return partes.join(' · ');
};

/* Taxa e id da simulação vindos dos detalhes — cada banco chama de um jeito. */
const taxaDe = of => {
  const d = of?.detalhes || {};
  const t = d.taxa ?? d.taxaMensal ?? d.taxa_mensal ?? d.monthlyRate;
  if (t === undefined || t === null || t === '') return '';
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0) return '';
  return `${String(n.toFixed(2)).replace('.', ',')}%`;
};
const idSimDe = of => {
  const d = of?.detalhes || {};
  return d.sim_id || d.id_simulador || d.simId || d.id_simulacao || d.config_id || '';
};

const contagens = computed(() => {
  const c = {
    consultando: 0,
    requerAutorizacao: 0,
    ofertas: 0,
    foraRoteiro: 0,
    indisponivel: 0,
  };
  SIM_BANKS.forEach(b => {
    const e = estadoDe(b);
    if (e === 'consultando') c.consultando += 1;
    else if (e === 'requer_autorizacao') c.requerAutorizacao += 1;
    else if (e === 'oferta') c.ofertas += 1;
    else if (e === 'indisponivel' || e === 'sem_oferta') c.indisponivel += 1;
  });
  return c;
});

const CINZA = 'rgba(161,161,161,0.7)';

/* Borda do card acompanha o estado (verde=oferta, âmbar=teimosinha,
   vermelho=indisponível) — igual às capturas. */
const bordaDe = b => {
  const e = estadoDe(b);
  if (e === 'oferta') return '#16a34a';
  if (e === 'indisponivel') return '#b91c1c';
  if (emTeimosinha(b)) return '#d97706';
  return '';
};

const clickCard = b => {
  if (estadoDe(b) !== 'oferta') return;
  aberto.value = aberto.value === b ? '' : b;
};

const pillDe = b => BANK_STATE_LABEL[estadoDe(b)] || '—';

/* ---------------------------------------------------- copiar (v44) ------ */
const copiarTexto = async (texto, aviso) => {
  try {
    await navigator.clipboard.writeText(texto);
  } catch (e) {
    // fallback: navegador sem clipboard API (ou sem permissão)
    const ta = document.createElement('textarea');
    ta.value = texto;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
  useAlert(aviso);
};

const copiarOferta = (banco, of) => {
  const partes = [
    `${BANK_STYLE[banco].label} — Valor liberado ${brl(of.valor_liberado)}`,
    `${of.prazo}x de ${brl(of.parcela)}`,
  ];
  const seg = of?.detalhes?.seguro;
  if (seg === true) partes.push('com seguro');
  else if (seg === false) partes.push('sem seguro');
  if (taxaDe(of)) partes.push(`taxa ${taxaDe(of)}`);
  copiarTexto(partes.join(' • '), 'Oferta copiada');
};

const copiarLink = b => copiarTexto(linkDe(b), 'Link copiado — envie ao cliente');

/* ------------------------------------------- SIMULAR por oferta (v44) ---
   Abre AO LADO da linha um card no estilo do outro simulador, COM chips de
   prazo (os prazos únicos das ofertas do banco; o inicial é o da linha
   clicada). "Simular novo valor" chama o simulador-api (simular-valor) com o
   prazo do chip ativo — recalcula SEM mexer no resultado persistido. */
const simAberta = ref(''); // `${banco}:${i}` da linha expandida
// por chave: { prazoAtivo, seguro, tipo, valor, busy, erro, resultado }
const simCards = ref({});

const chaveSim = (banco, i) => `${banco}:${i}`;

const prazosDe = banco =>
  [...new Set((dadosDe(banco)?.ofertas || []).map(o => o.prazo).filter(Boolean))].sort(
    (a, b) => a - b
  );

/* Oferta exibida no card: o resultado custom (se simulou valor novo) ou a
   oferta da LISTA que casa com prazo ativo + toggle de seguro. */
const ofertaDoCard = (banco, k) => {
  const st = simCards.value[k];
  if (!st) return null;
  if (st.resultado) return st.resultado;
  const ofs = (dadosDe(banco)?.ofertas || []).filter(o => o.prazo === st.prazoAtivo);
  return (
    ofs.find(o => o.detalhes?.seguro === st.seguro) ||
    ofs.find(o => o.detalhes?.seguro === undefined) ||
    ofs[0] ||
    null
  );
};

const abrirSimular = (banco, i, of) => {
  const k = chaveSim(banco, i);
  if (simAberta.value === k) {
    simAberta.value = '';
    return;
  }
  simCards.value[k] = {
    prazoAtivo: of?.prazo || prazosDe(banco)[0] || 0,
    seguro: of?.detalhes?.seguro !== false,
    tipo: 'liquido', // liquido | parcela
    valor: '',
    busy: false,
    erro: '',
    resultado: null, // oferta devolvida pelo simular-valor
  };
  simAberta.value = k;
};

/* Trocar chip de prazo (ou o seguro) mostra a oferta daquele prazo da lista
   e LIMPA o resultado custom anterior — regra do Kairo. */
const trocarPrazo = (k, prazo) => {
  const st = simCards.value[k];
  if (!st) return;
  st.prazoAtivo = prazo;
  st.resultado = null;
  st.erro = '';
};
const trocarSeguro = k => {
  const st = simCards.value[k];
  if (!st) return;
  st.resultado = null;
  st.erro = '';
};

const numDigitado = v => {
  // aceita "3.500,00", "3500.00" e "3500"
  const s = String(v || '').trim();
  if (!s) return null;
  const n = Number(s.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : null;
};

const simularValor = async (banco, i) => {
  const k = chaveSim(banco, i);
  const st = simCards.value[k];
  if (!st || !props.simulacaoId) return;
  st.busy = true;
  st.erro = '';
  try {
    const valor = numDigitado(st.valor);
    const base = ofertaDoCard(banco, k);
    const body = {
      prazo: st.prazoAtivo,
      seguro: st.seguro,
      tipo: valor ? st.tipo : 'maximo',
      valor,
      detalhes: base?.detalhes || {},
    };
    const { data } = await simuladorApi.post(
      `simulacoes/${props.simulacaoId}/bancos/${banco}/simular-valor`,
      body
    );
    if (data?.oferta) st.resultado = data.oferta;
  } catch (e) {
    st.erro =
      e.response?.data?.detail ||
      e.response?.data?.error ||
      'Falha ao simular este valor.';
  } finally {
    st.busy = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Contadores -->
    <div class="flex flex-wrap items-center text-xs gap-x-5 gap-y-2">
      <span v-for="s in STATUS_COUNTERS" :key="s.key" class="flex items-center gap-1.5">
        <span
          class="rounded-full size-2"
          :style="{ backgroundColor: contagens[s.key] ? s.color : CINZA }"
        />
        <span
          class="font-semibold"
          :class="contagens[s.key] ? 'text-n-slate-12' : 'text-n-slate-11'"
        >
          {{ contagens[s.key] }}
        </span>
        <span class="text-n-slate-11">{{ s.label }}</span>
      </span>
    </div>

    <!-- Chips de resultado (aparecem quando há simulação) -->
    <div v-if="bancos.length" class="flex flex-wrap items-center gap-2">
      <template v-for="b in SIM_BANKS" :key="`chip-${b}`">
        <button
          v-if="estadoDe(b) === 'oferta'"
          type="button"
          class="sd-chip-oferta"
          @click="emit('digitar', { banco: b, oferta: melhorOferta(b) })"
        >
          <span class="font-semibold">{{ BANK_STYLE[b].label }}</span>
          <span class="tabular-nums">{{ brl(dadosDe(b)?.valorMaximo) }}</span>
          <span class="sd-chip-digitar">digitar</span>
        </button>
        <span
          v-else-if="estadoDe(b) === 'consultando'"
          class="sd-chip-neutro"
        >
          <span class="i-lucide-loader-2 size-3 animate-spin" />
          {{ BANK_STYLE[b].label }}
        </span>
        <span
          v-else-if="estadoDe(b) === 'indisponivel'"
          class="sd-chip-erro"
        >
          {{ BANK_STYLE[b].label }}
        </span>
        <span v-else class="sd-chip-neutro">{{ BANK_STYLE[b].label }}</span>
      </template>
    </div>

    <!-- Cards -->
    <div
      v-if="mostrarCards"
      class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6"
    >
      <div
        v-for="b in SIM_BANKS"
        :key="b"
        class="flex flex-col overflow-hidden transition-colors border rounded-lg border-n-weak"
        :class="estadoDe(b) === 'oferta' ? 'cursor-pointer' : ''"
        :style="bordaDe(b) ? { borderColor: bordaDe(b) } : {}"
        role="button"
        tabindex="0"
        @click="clickCard(b)"
        @keydown.enter="clickCard(b)"
      >
        <!-- Faixa do banco: nome + pill de status -->
        <div
          class="flex items-center justify-between gap-2 px-3 py-3"
          :style="{ backgroundColor: BANK_STYLE[b].bg, color: BANK_STYLE[b].fg }"
        >
          <span class="text-base font-bold truncate">{{ BANK_STYLE[b].label }}</span>
          <span class="sd-pill">
            <span
              v-if="estadoDe(b) === 'consultando'"
              class="i-lucide-loader-2 size-3 animate-spin"
            />
            {{ pillDe(b) }}
          </span>
        </div>

        <!-- Corpo por estado -->
        <div class="flex flex-col flex-1 gap-2 px-4 py-4 bg-n-solid-2 min-h-[150px]">
          <!-- OFERTA -->
          <template v-if="estadoDe(b) === 'oferta'">
            <p class="text-[11px] font-medium tracking-wide uppercase text-n-slate-11">
              Máx. liberado
            </p>
            <p class="text-2xl font-bold text-n-slate-12 tabular-nums">
              {{ brl(dadosDe(b)?.valorMaximo) }}
            </p>
            <p v-if="melhorOferta(b)" class="text-sm text-n-slate-11 tabular-nums">
              {{ melhorOferta(b).prazo }}x {{ brl(melhorOferta(b).parcela) }}
            </p>
            <p class="mt-auto text-[11px] text-n-slate-11">
              {{ (dadosDe(b)?.ofertas || []).length }} oferta(s) — clique para ver
            </p>
          </template>

          <!-- TEIMOSINHA (consultando com tentativa > 1) -->
          <template v-else-if="emTeimosinha(b)">
            <p class="text-sm font-medium text-n-slate-12">
              Teimosinha em segundo plano…
            </p>
            <p class="font-mono text-sm text-n-slate-12">
              Tentativa {{ dadosDe(b)?.tentativa }}/{{ dadosDe(b)?.maxTentativas }}
            </p>
            <p class="text-xs text-n-slate-11 tabular-nums">
              consultando há {{ elapsedDe(b) }}s
              <template v-if="mediaDe(b)">~{{ mediaDe(b) }}s méd.</template>
            </p>
            <div class="w-full h-1 overflow-hidden rounded bg-n-alpha-2">
              <div
                class="h-full rounded bg-n-brand transition-all"
                :style="{ width: `${progressoDe(b)}%` }"
              />
            </div>
            <button
              type="button"
              class="sd-btn-card mt-1"
              @click.stop="emit('atualizar-status', b)"
            >
              <span class="i-lucide-rotate-cw size-3.5" />
              Atualizar status
            </button>
            <button
              v-if="linkDe(b)"
              type="button"
              class="sd-btn-card"
              @click.stop="copiarLink(b)"
            >
              <span class="i-lucide-link size-3.5" />
              Copiar link
            </button>
            <button
              type="button"
              class="sd-btn-ressimular mt-auto"
              @click.stop="emit('ressimular', b)"
            >
              <span class="i-lucide-refresh-cw size-3.5" />
              Ressimular
            </button>
          </template>

          <!-- CONSULTANDO (1ª tentativa) -->
          <template v-else-if="estadoDe(b) === 'consultando'">
            <p class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
              <span class="i-lucide-loader-2 size-4 animate-spin text-n-slate-11" />
              Consultando…
            </p>
            <p class="text-xs text-n-slate-11 tabular-nums">
              consultando há {{ elapsedDe(b) }}s
              <template v-if="mediaDe(b)">~{{ mediaDe(b) }}s méd.</template>
            </p>
            <div class="w-full h-1 overflow-hidden rounded bg-n-alpha-2">
              <div
                class="h-full rounded bg-n-brand transition-all"
                :style="{ width: `${progressoDe(b)}%` }"
              />
            </div>
          </template>

          <!-- INDISPONÍVEL -->
          <template v-else-if="estadoDe(b) === 'indisponivel'">
            <p class="text-sm font-semibold text-n-slate-12">
              Simulação não concluída
            </p>
            <p class="text-xs break-words text-n-slate-11">
              {{ dadosDe(b)?.erro || 'O banco não respondeu.' }}
            </p>
            <button
              v-if="linkDe(b)"
              type="button"
              class="sd-btn-card"
              @click.stop="copiarLink(b)"
            >
              <span class="i-lucide-link size-3.5" />
              Copiar link
            </button>
            <button
              type="button"
              class="sd-btn-ressimular mt-auto"
              @click.stop="emit('ressimular', b)"
            >
              <span class="i-lucide-refresh-cw size-3.5" />
              Ressimular
            </button>
          </template>

          <!-- SEM OFERTA -->
          <template v-else-if="estadoDe(b) === 'sem_oferta'">
            <p class="text-sm font-semibold text-n-slate-12">Sem oferta</p>
            <p class="text-xs text-n-slate-11">
              O banco respondeu, mas não liberou valor para este CPF.
            </p>
            <button
              v-if="linkDe(b)"
              type="button"
              class="sd-btn-card"
              @click.stop="copiarLink(b)"
            >
              <span class="i-lucide-link size-3.5" />
              Copiar link
            </button>
            <button
              type="button"
              class="sd-btn-ressimular mt-auto"
              @click.stop="emit('ressimular', b)"
            >
              <span class="i-lucide-refresh-cw size-3.5" />
              Ressimular
            </button>
          </template>

          <!-- REQUER AUTORIZAÇÃO -->
          <template v-else-if="estadoDe(b) === 'requer_autorizacao'">
            <p class="text-sm font-semibold text-n-amber-11">
              Requer autorização
            </p>
            <p class="text-xs text-n-slate-11">
              O cliente precisa autorizar a consulta neste banco.
            </p>
            <!-- v44: o vendedor copia o link e manda pro cliente autorizar -->
            <button
              v-if="linkDe(b)"
              type="button"
              class="sd-btn-copiar-link"
              @click.stop="copiarLink(b)"
            >
              <span class="i-lucide-link size-3.5" />
              Copiar link
            </button>
            <button
              type="button"
              class="sd-btn-ressimular mt-auto"
              @click.stop="emit('ressimular', b)"
            >
              <span class="i-lucide-refresh-cw size-3.5" />
              Ressimular
            </button>
          </template>

          <!-- AGUARDANDO -->
          <template v-else>
            <p class="mt-auto mb-auto text-xs text-center text-n-slate-11">
              Aguardando simulação
            </p>
          </template>
        </div>
      </div>
    </div>

    <!-- Painel expandido: todas as ofertas do banco clicado -->
    <div
      v-if="aberto && estadoDe(aberto) === 'oferta'"
      class="overflow-hidden border rounded-lg border-n-weak"
    >
      <div
        class="flex items-center justify-between px-4 py-3"
        :style="{ backgroundColor: BANK_STYLE[aberto].bg, color: BANK_STYLE[aberto].fg }"
      >
        <span class="text-sm font-bold">
          Ofertas {{ BANK_STYLE[aberto].label }}
        </span>
        <button
          type="button"
          class="sd-pill"
          @click="aberto = ''"
        >
          <span class="i-lucide-x size-3" />
          Fechar
        </button>
      </div>
      <ul class="flex flex-col bg-n-solid-2">
        <li
          v-for="(of, i) in ofertasOrdenadas(aberto)"
          :key="i"
          :class="i > 0 ? 'border-t border-n-weak' : ''"
        >
          <!-- v44: com o card aberto a linha vira duas colunas — infos+botões
               à esquerda (eles "chegam para o lado") e o card à direita.
               Em tela estreita o card cai para baixo. -->
          <div
            class="flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-start"
          >
            <div
              class="flex items-center justify-between gap-3 transition-all duration-300"
              :class="simAberta === chaveSim(aberto, i)
                ? 'lg:w-[300px] lg:shrink-0 lg:flex-col lg:items-start'
                : 'flex-1 flex-wrap'"
            >
              <div class="flex flex-col gap-0.5">
                <span class="text-base font-semibold text-n-slate-12 tabular-nums">
                  {{ brl(of.valor_liberado) }}
                </span>
                <span class="text-xs text-n-slate-11 tabular-nums">
                  {{ of.prazo }}x {{ brl(of.parcela) }}
                  <template v-if="detalheResumo(of)"> · {{ detalheResumo(of) }}</template>
                </span>
              </div>
              <!-- SIMULAR (amarelo) · COPIAR OFERTA (azul) · DIGITAR (verde) -->
              <div class="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  class="sd-btn-amarelo"
                  @click="abrirSimular(aberto, i, of)"
                >
                  <span class="i-lucide-calculator size-4" />
                  Simular
                </button>
                <button
                  type="button"
                  class="sd-btn-copiar"
                  @click="copiarOferta(aberto, of)"
                >
                  <span class="i-lucide-copy size-4" />
                  Copiar oferta
                </button>
                <button
                  type="button"
                  class="sd-btn-digitar"
                  @click="emit('digitar', { banco: aberto, oferta: of })"
                >
                  <span class="i-lucide-pen-line size-4" />
                  Digitar
                </button>
              </div>
            </div>

            <!-- Card SIMULAR ao lado (estilo do outro simulador, com chips) -->
            <Transition name="sd-slide">
              <div
                v-if="simAberta === chaveSim(aberto, i) && simCards[chaveSim(aberto, i)]"
                class="flex-1 min-w-0"
              >
                <div class="sd-sim-card">
                  <div class="flex items-start justify-between gap-2">
                    <div class="flex flex-col gap-1">
                      <span class="text-xs text-n-slate-11">Valor liberado</span>
                      <span
                        class="text-2xl font-bold tabular-nums"
                        style="color: #34d399"
                      >
                        {{ brl(ofertaDoCard(aberto, chaveSim(aberto, i))?.valor_liberado) }}
                      </span>
                    </div>
                    <div class="flex flex-wrap items-center justify-end gap-2">
                      <span
                        v-if="detalheResumo(ofertaDoCard(aberto, chaveSim(aberto, i)))"
                        class="sd-sim-tag"
                      >
                        {{ detalheResumo(ofertaDoCard(aberto, chaveSim(aberto, i))) }}
                      </span>
                      <span
                        v-if="simCards[chaveSim(aberto, i)].resultado"
                        class="sd-sim-tag"
                      >
                        SIMULADO
                      </span>
                    </div>
                  </div>

                  <div class="grid grid-cols-3 gap-2">
                    <div class="sd-sim-fato">
                      <span class="text-xs text-n-slate-11">Prazo</span>
                      <span class="text-base font-bold text-n-slate-12 tabular-nums">
                        {{ ofertaDoCard(aberto, chaveSim(aberto, i))?.prazo || '—' }}x
                      </span>
                    </div>
                    <div class="sd-sim-fato">
                      <span class="text-xs text-n-slate-11">Parcela</span>
                      <span class="text-base font-bold text-n-slate-12 tabular-nums">
                        {{ brl(ofertaDoCard(aberto, chaveSim(aberto, i))?.parcela) }}
                      </span>
                    </div>
                    <div class="sd-sim-fato">
                      <span class="text-xs text-n-slate-11">Taxa</span>
                      <span class="text-base font-bold text-n-slate-12 tabular-nums">
                        {{ taxaDe(ofertaDoCard(aberto, chaveSim(aberto, i))) || '—' }}
                      </span>
                    </div>
                  </div>

                  <p
                    v-if="idSimDe(ofertaDoCard(aberto, chaveSim(aberto, i)))"
                    class="sd-sim-id"
                  >
                    ID da simulação:
                    <strong class="break-all">
                      {{ idSimDe(ofertaDoCard(aberto, chaveSim(aberto, i))) }}
                    </strong>
                  </p>

                  <!-- Chips de prazo (os prazos únicos das ofertas do banco) -->
                  <div class="flex flex-wrap items-center gap-1.5">
                    <button
                      v-for="pz in prazosDe(aberto)"
                      :key="pz"
                      type="button"
                      class="sd-sim-prazo"
                      :class="simCards[chaveSim(aberto, i)].prazoAtivo === pz ? 'is-on' : ''"
                      @click="trocarPrazo(chaveSim(aberto, i), pz)"
                    >
                      {{ pz }}x
                    </button>
                  </div>

                  <div class="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      class="sd-btn-copiar"
                      @click="copiarOferta(aberto, ofertaDoCard(aberto, chaveSim(aberto, i)))"
                    >
                      <span class="i-lucide-copy size-4" />
                      Copiar
                    </button>
                    <button
                      type="button"
                      class="sd-btn-digitar"
                      @click="
                        emit('digitar', {
                          banco: aberto,
                          oferta: ofertaDoCard(aberto, chaveSim(aberto, i)),
                        })
                      "
                    >
                      <span class="i-lucide-pen-line size-4" />
                      Digitar
                    </button>
                  </div>

                  <!-- Simular novo valor -->
                  <div class="sd-sim-novo">
                    <span class="text-sm font-semibold text-n-slate-12">
                      Simular novo valor
                    </span>
                    <div class="flex flex-wrap items-center gap-2">
                      <div class="sd-sim-toggle">
                        <button
                          type="button"
                          :class="simCards[chaveSim(aberto, i)].tipo === 'liquido' ? 'is-on' : ''"
                          @click="simCards[chaveSim(aberto, i)].tipo = 'liquido'"
                        >
                          Valor líquido
                        </button>
                        <button
                          type="button"
                          :class="simCards[chaveSim(aberto, i)].tipo === 'parcela' ? 'is-on' : ''"
                          @click="simCards[chaveSim(aberto, i)].tipo = 'parcela'"
                        >
                          Valor da parcela
                        </button>
                      </div>
                      <label class="flex items-center gap-1.5 text-sm text-n-slate-12">
                        <input
                          v-model="simCards[chaveSim(aberto, i)].seguro"
                          type="checkbox"
                          class="size-4"
                          @change="trocarSeguro(chaveSim(aberto, i))"
                        />
                        Com seguro
                      </label>
                    </div>
                    <div class="flex flex-wrap items-center gap-2">
                      <input
                        v-model="simCards[chaveSim(aberto, i)].valor"
                        type="text"
                        inputmode="decimal"
                        placeholder="R$ 0,00 (vazio = máximo)"
                        class="sd-input flex-1 min-w-[180px]"
                        @keydown.enter="simularValor(aberto, i)"
                      />
                      <button
                        type="button"
                        class="sd-btn-amarelo"
                        :disabled="simCards[chaveSim(aberto, i)].busy"
                        @click="simularValor(aberto, i)"
                      >
                        <span
                          :class="simCards[chaveSim(aberto, i)].busy
                            ? 'i-lucide-loader-2 animate-spin'
                            : 'i-lucide-calculator'"
                          class="size-4"
                        />
                        {{ simCards[chaveSim(aberto, i)].busy ? 'Simulando…' : 'Simular' }}
                      </button>
                    </div>
                    <p
                      v-if="simCards[chaveSim(aberto, i)].erro"
                      class="p-2 text-xs rounded-md border border-n-ruby-7 bg-n-ruby-2 text-n-ruby-11"
                    >
                      {{ simCards[chaveSim(aberto, i)].erro }}
                    </p>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
        </li>
      </ul>
    </div>

    <button
      type="button"
      class="inline-flex items-center gap-1 text-xs transition-colors w-fit text-n-slate-11 hover:text-n-slate-12"
      @click="mostrarCards = !mostrarCards"
    >
      <span
        :class="mostrarCards ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        class="size-3.5"
      />
      {{ mostrarCards ? 'Ocultar cards' : 'Mostrar cards' }}
    </button>
  </div>
</template>
